import React, { useState, useRef, useEffect } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { useAgentChat, ChatEntry } from './hooks/useAgentChat';

// ─── Tool label mapping ───
const TOOL_LABELS: Record<string, string> = {
	get_rac_policies: 'RAC Policies',
	get_rac_policy_by_persona_sector: 'Policy Lookup',
	get_responsible_lending_formulas: 'Lending Formulas',
	get_active_responsible_lending: 'Active Lending Config',
	get_deviation_rules: 'Deviation Rules',
	get_scorecards: 'Scorecards',
	get_sectors: 'Sectors',
	get_personas: 'Personas',
	get_product_types: 'Product Types',
	get_application_statistics: 'App Statistics',
	get_application_by_id: 'App Lookup',
	get_approval_rate: 'Approval Rate',
};

// ─── Suggested prompts organized by category ───
const PROMPT_CATEGORIES = [
	{
		title: 'Policies & Rules',
		color: 'blue',
		prompts: [
			'What RAC policies are currently active?',
			'Show me the RAC policy for salaried employees in the private sector',
			'What are the current deviation rules?',
			'List all responsible lending formulas',
			'What is the active responsible lending configuration?',
		],
	},
	{
		title: 'Scorecards & Risk',
		color: 'purple',
		prompts: [
			'Show me all active scorecards',
			'What risk grades are defined in the scoring system?',
			'Explain the scorecard attributes and their weights',
		],
	},
	{
		title: 'Segments & Products',
		color: 'teal',
		prompts: [
			'What borrower personas are configured?',
			'List all available lending sectors',
			'What loan product types does the system support?',
		],
	},
	{
		title: 'Analytics',
		color: 'green',
		prompts: [
			'What are the current loan application statistics?',
			'What is the approval rate for the last 30 days?',
			'Show me the breakdown of applications by status',
		],
	},
	{
		title: 'Comparative & Analytical',
		color: 'orange',
		prompts: [
			'Compare the RAC policies across different sectors',
			'Which persona has the strictest lending criteria?',
			'Summarize the responsible lending limits for salaried employees',
		],
	},
];

const COLOR_MAP: Record<string, { bg: string; border: string; text: string; chip: string }> = {
	blue: { bg: 'bg-blue-50', border: 'border-blue-200', text: 'text-blue-700', chip: 'bg-blue-100 text-blue-700 hover:bg-blue-200' },
	purple: { bg: 'bg-purple-50', border: 'border-purple-200', text: 'text-purple-700', chip: 'bg-purple-100 text-purple-700 hover:bg-purple-200' },
	teal: { bg: 'bg-teal-50', border: 'border-teal-200', text: 'text-teal-700', chip: 'bg-teal-100 text-teal-700 hover:bg-teal-200' },
	green: { bg: 'bg-green-50', border: 'border-green-200', text: 'text-green-700', chip: 'bg-green-100 text-green-700 hover:bg-green-200' },
	orange: { bg: 'bg-orange-50', border: 'border-orange-200', text: 'text-orange-700', chip: 'bg-orange-100 text-orange-700 hover:bg-orange-200' },
};

// ─── Guardrail flag helpers ───
function hasWarningFlag(flags?: string[]): boolean {
	if (!flags) return false;
	return flags.some(
		(f) => f === 'input_blocked' || f === 'prompt_injection_detected' || f === 'pii_request_detected'
	);
}

function getGuardrailLabel(flag: string): string {
	switch (flag) {
		case 'input_blocked':
			return 'Message blocked by safety guardrails';
		case 'prompt_injection_detected':
			return 'Prompt injection detected';
		case 'pii_request_detected':
			return 'Personal data request detected';
		case 'write_operation_requested':
			return 'Write operation requested';
		case 'llm_error':
			return 'AI service error';
		case 'max_iterations_reached':
			return 'Response may be incomplete — question was too complex';
		default:
			if (flag.startsWith('tool_access_denied:')) {
				return `Access denied: ${flag.replace('tool_access_denied:', '')}`;
			}
			return flag;
	}
}

// ─── Main Component ───
interface AgentChatProps {
	baseUrl: string;
}

const AgentChat: React.FC<AgentChatProps> = ({ baseUrl }) => {
	const [token, setToken] = useState(() => localStorage.getItem('agent_token') || '');
	const [tokenInput, setTokenInput] = useState(() => localStorage.getItem('agent_token') || '');
	const [showTokenInput, setShowTokenInput] = useState(!localStorage.getItem('agent_token'));

	const { messages, isLoading, error, sendMessage, clearChat } = useAgentChat({
		baseUrl,
		token,
	});

	const messagesEndRef = useRef<HTMLDivElement>(null);
	const inputRef = useRef<HTMLInputElement>(null);
	const [input, setInput] = useState('');
	const [showSuggestions, setShowSuggestions] = useState(true);

	// Auto-scroll to bottom on new messages
	useEffect(() => {
		messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
	}, [messages, isLoading]);

	// Focus input on mount
	useEffect(() => {
		if (token && inputRef.current) {
			inputRef.current.focus();
		}
	}, [token]);

	const handleSaveToken = () => {
		const trimmed = tokenInput.trim();
		if (!trimmed) return;
		localStorage.setItem('agent_token', trimmed);
		setToken(trimmed);
		setShowTokenInput(false);
	};

	const handleClearToken = () => {
		localStorage.removeItem('agent_token');
		setToken('');
		setTokenInput('');
		setShowTokenInput(true);
		clearChat();
	};

	const handleSend = () => {
		const trimmed = input.trim();
		if (!trimmed || isLoading) return;
		sendMessage(trimmed);
		setInput('');
	};

	const handleSuggestion = (text: string) => {
		if (isLoading) return;
		sendMessage(text);
	};

	const handleKeyDown = (e: React.KeyboardEvent) => {
		if (e.key === 'Enter' && !e.shiftKey) {
			e.preventDefault();
			handleSend();
		}
	};

	// ─── Token setup screen ───
	if (showTokenInput || !token) {
		return (
			<div className="bg-white rounded-lg border border-gray-200 shadow-sm p-8 max-w-lg mx-auto mt-8">
				<div className="text-center mb-6">
					<div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-3">
						<svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
							<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z" />
						</svg>
					</div>
					<h3 className="text-lg font-semibold text-gray-900">Authentication Required</h3>
					<p className="text-sm text-gray-500 mt-1">Enter your JWT token or API key to access the SOAR AI Agent.</p>
				</div>
				<div className="space-y-3">
					<input
						type="password"
						value={tokenInput}
						onChange={(e) => setTokenInput(e.target.value)}
						onKeyDown={(e) => e.key === 'Enter' && handleSaveToken()}
						placeholder="Paste your token here..."
						className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
					/>
					<button
						onClick={handleSaveToken}
						disabled={!tokenInput.trim()}
						className="w-full px-4 py-2 bg-blue-600 text-white rounded-md text-sm font-medium hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors">
						Connect to AI Agent
					</button>
				</div>
				<p className="text-xs text-gray-400 mt-3 text-center">Token is stored locally in your browser.</p>
			</div>
		);
	}

	// ─── Chat interface ───
	return (
		<div className="bg-white rounded-lg border border-gray-200 shadow-sm flex flex-col" style={{ height: 'calc(100vh - 280px)', minHeight: '500px' }}>
			{/* Chat header */}
			<div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 flex-shrink-0">
				<div className="flex items-center gap-2">
					<div className="w-2 h-2 bg-green-500 rounded-full" />
					<span className="text-sm font-medium text-gray-700">SOAR AI Agent</span>
				</div>
				<div className="flex items-center gap-2">
					<button
						onClick={clearChat}
						disabled={messages.length === 0 && !error}
						className="px-3 py-1 text-xs text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-md transition-colors disabled:opacity-40 disabled:cursor-not-allowed">
						Clear Chat
					</button>
					<button
						onClick={handleClearToken}
						className="px-3 py-1 text-xs text-red-500 hover:text-red-700 hover:bg-red-50 rounded-md transition-colors">
						Disconnect
					</button>
				</div>
			</div>

			{/* Messages area */}
			<div className="flex-1 overflow-y-auto px-4 py-4 space-y-4">
				{/* Suggested prompts — always available, collapsible after first message */}
				<div className="border border-gray-200 rounded-lg overflow-hidden">
					{messages.length === 0 ? (
						/* Expanded view when chat is empty */
						<div className="space-y-6 p-4">
							<div className="text-center">
								<h3 className="text-lg font-semibold text-gray-800">How can I help you?</h3>
								<p className="text-sm text-gray-500 mt-1">
									Ask about lending rules, policies, scorecards, or analytics.
								</p>
							</div>
							<SuggestionsGrid onSelect={handleSuggestion} isLoading={isLoading} />
						</div>
					) : (
						/* Collapsible panel after messages exist */
						<>
							<button
								onClick={() => setShowSuggestions((prev) => !prev)}
								className="w-full flex items-center justify-between px-4 py-2.5 bg-gray-50 hover:bg-gray-100 transition-colors text-sm">
								<span className="font-medium text-gray-600">Suggested Prompts</span>
								<svg
									className={`w-4 h-4 text-gray-400 transition-transform ${showSuggestions ? 'rotate-180' : ''}`}
									fill="none"
									stroke="currentColor"
									viewBox="0 0 24 24">
									<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
								</svg>
							</button>
							{showSuggestions && (
								<div className="p-4 border-t border-gray-200">
									<SuggestionsGrid onSelect={handleSuggestion} isLoading={isLoading} />
								</div>
							)}
						</>
					)}
				</div>

				{/* Chat messages */}
				{messages.map((msg) => (
					<MessageBubble key={msg.id} message={msg} />
				))}

				{/* Loading indicator */}
				{isLoading && (
					<div className="flex items-start gap-3">
						<div className="w-7 h-7 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0 mt-0.5">
							<svg className="w-4 h-4 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
								<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
							</svg>
						</div>
						<div className="bg-gray-100 rounded-lg px-4 py-3">
							<div className="flex items-center gap-2">
								<div className="flex gap-1">
									<span className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
									<span className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
									<span className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
								</div>
								<span className="text-sm text-gray-500">Thinking...</span>
							</div>
						</div>
					</div>
				)}

				{/* Error banner */}
				{error && (
					<div className="bg-red-50 border border-red-200 rounded-lg px-4 py-3">
						<div className="flex items-center gap-2">
							<svg className="w-4 h-4 text-red-500 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
								<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
							</svg>
							<span className="text-sm text-red-700">{error}</span>
						</div>
					</div>
				)}

				<div ref={messagesEndRef} />
			</div>

			{/* Input area */}
			<div className="border-t border-gray-200 px-4 py-3 flex-shrink-0">
				<div className="flex items-center gap-2">
					<input
						ref={inputRef}
						value={input}
						onChange={(e) => setInput(e.target.value)}
						onKeyDown={handleKeyDown}
						placeholder="Ask about lending rules, policies, or analytics..."
						disabled={isLoading}
						className="flex-1 px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-50 disabled:text-gray-400"
					/>
					<button
						onClick={handleSend}
						disabled={isLoading || !input.trim()}
						className="px-4 py-2 bg-blue-600 text-white rounded-md text-sm font-medium hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center gap-1.5">
						<span>Send</span>
						<svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
							<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
						</svg>
					</button>
				</div>
			</div>
		</div>
	);
};

// ─── Suggestions Grid Sub-component ───
const SuggestionsGrid: React.FC<{ onSelect: (text: string) => void; isLoading: boolean }> = ({
	onSelect,
	isLoading,
}) => (
	<div className="space-y-4">
		{PROMPT_CATEGORIES.map((category) => {
			const colors = COLOR_MAP[category.color] || COLOR_MAP.blue;
			return (
				<div key={category.title}>
					<h4 className={`text-xs font-semibold uppercase tracking-wider ${colors.text} mb-2`}>
						{category.title}
					</h4>
					<div className="flex flex-wrap gap-2">
						{category.prompts.map((prompt) => (
							<button
								key={prompt}
								onClick={() => !isLoading && onSelect(prompt)}
								disabled={isLoading}
								className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${colors.chip}`}>
								{prompt}
							</button>
						))}
					</div>
				</div>
			);
		})}
	</div>
);

// ─── Message Bubble Sub-component ───
const MessageBubble: React.FC<{ message: ChatEntry }> = ({ message }) => {
	const isUser = message.role === 'user';
	const isWarning = hasWarningFlag(message.guardrailFlags);

	if (isUser) {
		return (
			<div className="flex justify-end">
				<div className="max-w-[75%] bg-blue-600 text-white rounded-lg px-4 py-2.5">
					<p className="text-sm whitespace-pre-wrap">{message.content}</p>
				</div>
			</div>
		);
	}

	// Assistant message
	return (
		<div className="flex items-start gap-3">
			<div className="w-7 h-7 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0 mt-0.5">
				<svg className="w-4 h-4 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
					<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
				</svg>
			</div>
			<div className="max-w-[85%] space-y-2">
				{/* Guardrail warnings */}
				{isWarning && (
					<div className="bg-yellow-50 border border-yellow-200 rounded-md px-3 py-2">
						{message.guardrailFlags!
							.filter((f) => ['input_blocked', 'prompt_injection_detected', 'pii_request_detected'].includes(f))
							.map((flag) => (
								<div key={flag} className="flex items-center gap-1.5 text-xs text-yellow-700">
									<svg className="w-3.5 h-3.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
										<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z" />
									</svg>
									<span>{getGuardrailLabel(flag)}</span>
								</div>
							))}
					</div>
				)}

				{/* Response content */}
				<div className={`bg-gray-50 rounded-lg px-4 py-3 ${isWarning ? 'border border-yellow-200' : ''}`}>
					<div className="prose prose-sm max-w-none text-gray-800 [&_table]:w-full [&_table]:border-collapse [&_th]:bg-gray-100 [&_th]:border [&_th]:border-gray-300 [&_th]:px-3 [&_th]:py-1.5 [&_th]:text-left [&_th]:text-xs [&_th]:font-semibold [&_td]:border [&_td]:border-gray-200 [&_td]:px-3 [&_td]:py-1.5 [&_td]:text-xs [&_code]:bg-gray-200 [&_code]:px-1 [&_code]:py-0.5 [&_code]:rounded [&_code]:text-xs [&_pre]:bg-gray-800 [&_pre]:text-gray-100 [&_pre]:rounded-md [&_pre]:p-3 [&_pre]:overflow-x-auto [&_pre_code]:bg-transparent [&_pre_code]:p-0 [&_ul]:list-disc [&_ul]:pl-5 [&_ol]:list-decimal [&_ol]:pl-5 [&_li]:my-0.5 [&_h2]:text-base [&_h2]:font-semibold [&_h2]:mt-3 [&_h2]:mb-1 [&_h3]:text-sm [&_h3]:font-semibold [&_h3]:mt-2 [&_h3]:mb-1 [&_p]:my-1">
						<ReactMarkdown remarkPlugins={[remarkGfm]}>{message.content}</ReactMarkdown>
					</div>
				</div>

				{/* Tools used chips */}
				{message.toolsUsed && message.toolsUsed.length > 0 && (
					<div className="flex flex-wrap gap-1.5">
						{message.toolsUsed.map((tool) => (
							<span
								key={tool}
								className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-medium bg-gray-100 text-gray-600 border border-gray-200">
								{TOOL_LABELS[tool] || tool}
							</span>
						))}
					</div>
				)}

				{/* Non-warning guardrail flags (info-level) */}
				{message.guardrailFlags &&
					message.guardrailFlags.length > 0 &&
					message.guardrailFlags.some(
						(f) =>
							!['input_blocked', 'prompt_injection_detected', 'pii_request_detected'].includes(f) &&
							!f.startsWith('pii_')
					) && (
						<div className="flex flex-wrap gap-1.5">
							{message.guardrailFlags
								.filter(
									(f) =>
										!['input_blocked', 'prompt_injection_detected', 'pii_request_detected'].includes(f) &&
										!f.startsWith('pii_')
								)
								.map((flag) => (
									<span
										key={flag}
										className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-medium bg-yellow-50 text-yellow-700 border border-yellow-200">
										{getGuardrailLabel(flag)}
									</span>
								))}
						</div>
					)}
			</div>
		</div>
	);
};

export default AgentChat;
